import Cookies from 'js-cookie';
import { useNavigate } from 'react-router';

import { getStoredConsent } from '../components/cookies/cookieUtils';

type RouterState = {
	redirectTo: string;
	redirectParams?: string;
};

const PRE_LOGIN_ROUTER_STATE_COOKIE = 'TS_GO_PRE_LOGIN_ROUTER_STATE';
export const isHttpLocation = globalThis.location && globalThis.location.protocol === 'http:';

const COOKIE_DEFAULTS = {
	sameSite: 'lax' as const,
	secure: !isHttpLocation,
};

type LockedAccountType = 'password' | 'activation' | 'mfa' | 'accountNotCreated';

type SigninMessageState = {
	heading: string;
	message: string;
	subMessage?: string;
	supportEmail?: string;
};

const userAccountLocked = 'Your user account has been locked';
const emailSupportUnlockMsg = 'Please email Customer Support to unlock your account.';
const supportEmail = 'support@mail.virtualcards.business.hsbc.com';

const LOCKED_ACCOUNT_MESSAGES: Record<LockedAccountType, SigninMessageState> = {
	password: {
		heading: userAccountLocked,
		message: "You've entered an invalid email or password too many times.",
		subMessage: emailSupportUnlockMsg,
		supportEmail,
	},
	activation: {
		heading: userAccountLocked,
		message: "You've entered the wrong activation code too many times.",
		subMessage: emailSupportUnlockMsg,
		supportEmail,
	},
	mfa: {
		heading: userAccountLocked,
		message: "You've entered the wrong code too many times.",
		subMessage: emailSupportUnlockMsg,
		supportEmail,
	},
	accountNotCreated: {
		heading: "You've not completed your activation",
		message: "So you won't be able to proceed any further at this time.",
		subMessage: 'Please email Customer Support to resolve this issue.',
		supportEmail,
	},
};

export const useSigninMessageNavigation = () => {
	const navigate = useNavigate();

	const navigateToLockedAccountMessage = (type: LockedAccountType): void => {
		navigateToSigninMessage(LOCKED_ACCOUNT_MESSAGES[type]);
	};

	const navigateToSigninMessage = (state: SigninMessageState): void => {
		void navigate('/signin/message', { state });
	};

	return { navigateToLockedAccountMessage, navigateToSigninMessage };
};

export const savePreLoginRouterState = (state: RouterState): void => {
	const { preferences } = getStoredConsent();

	// on Amex, we set cookie regardless
	// for hsbc we set it  only  if preferences are accepted on the cookie banner
	if (globalThis.cardProvider.IS_AMEX_PROVIDER_SETUP || preferences) {
		const inFiveMinutes = new Date(Date.now() + 5 * 60 * 1000);
		Cookies.remove(PRE_LOGIN_ROUTER_STATE_COOKIE, COOKIE_DEFAULTS);
		Cookies.set(PRE_LOGIN_ROUTER_STATE_COOKIE, JSON.stringify(state), {
			...COOKIE_DEFAULTS,
			expires: inFiveMinutes,
		});
	}
};

export const getPreLoginRouterState = (): RouterState => {
	const routerStateCookie = Cookies.get(PRE_LOGIN_ROUTER_STATE_COOKIE);
	const defaultState: RouterState = { redirectTo: '', redirectParams: '' };

	if (routerStateCookie) {
		try {
			removePreLoginRouterState();
			return JSON.parse(routerStateCookie) as RouterState;
		} catch {
			return defaultState;
		}
	}

	return defaultState;
};

export const removePreLoginRouterState = (): void => {
	Cookies.remove(PRE_LOGIN_ROUTER_STATE_COOKIE, COOKIE_DEFAULTS);
};
